import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GetExamEventByIdDocument,
  GetExamEventExamineesForMonitoringDocument,
  useEcPostponeExamEventTimeMutation,
} from '@exam-center/graphql';
import { useServerClock } from '@exam-center/server-time';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormButton,
  FormNumberInput,
  FormTextarea,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  TimePostponementAlert,
  TimePostponementSessionPostponedTime,
  TimePostponementSessionTime,
} from '../../components';
import type { PostponeTimeErrorCode, TimePostponementErrorState } from '../../types';

import { TimePostponementFormSchema, type TimePostponementFormType } from './schema';
import { mapErrorCodeToMessage, mapOriginalSessions, mapPostponedSessions } from './utils';

export interface TimePostponementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TimePostponementModal(props: Readonly<TimePostponementModalProps>) {
  const { isOpen, onClose } = props;

  const { examEvent } = useExamEventDetails();
  const { showSnackbar } = useSnackbar();
  const serverNow = useServerClock();
  const [errorState, setErrorState] = useState<TimePostponementErrorState | undefined>({
    alertError: undefined,
    sessionTimeError: undefined,
  });

  const formContext = useForm<TimePostponementFormType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: undefined,
      remark: '',
    },
    resolver: zodResolver(TimePostponementFormSchema),
  });

  const originalSessions = mapOriginalSessions(examEvent.sessions).map((session) => ({
    ...session,
    isStarted: session.start !== null && serverNow >= session.start,
  }));

  const adjustedAmount = formContext.watch('amount');

  const postponedSessions = useMemo(() => {
    const isValidAdjustAmount =
      TimePostponementFormSchema.shape.amount.safeParse(adjustedAmount).success;

    if (!isValidAdjustAmount || adjustedAmount === undefined) {
      return originalSessions.map((session) => ({
        ...session,
        start: null,
        end: null,
      }));
    }

    return mapPostponedSessions(originalSessions, adjustedAmount);
  }, [originalSessions, adjustedAmount]);

  const clearErrorState = () => {
    setErrorState({
      alertError: undefined,
      sessionTimeError: undefined,
    });
  };

  const handleClose = () => {
    formContext.reset();
    clearErrorState();
    onClose();
  };

  const [postponeExamEventTime, { loading: postponeExamEventTimeLoading }] =
    useEcPostponeExamEventTimeMutation({
      refetchQueries: [GetExamEventByIdDocument, GetExamEventExamineesForMonitoringDocument],
      awaitRefetchQueries: true,
    });

  const onSubmit = async (values: TimePostponementFormType) => {
    await postponeExamEventTime({
      variables: {
        postponeExamEventTimeInput: {
          examEventId: examEvent.id,
          postponeDurationInMinutes: values.amount,
          postponementRemark: values.remark.trim(),
          lastPostponedAt: examEvent.metadata.lastPostponedAt ?? null,
        },
      },
      onCompleted: () => {
        handleClose();
        showSnackbar({
          message: 'เลื่อนเวลาสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: (error) => {
        const errorCode = error?.graphQLErrors?.[0]?.extensions?.code as PostponeTimeErrorCode;

        if (!errorCode) {
          showSnackbar({
            message: 'เกิดข้อผิดพลาดในการเลื่อนเวลาสอบ',
            variant: 'error',
          });
          return;
        }

        setErrorState(mapErrorCodeToMessage(errorCode));
      },
    });
  };

  useEffect(() => {
    const subscription = formContext.watch((_, { name }) => {
      if (name === 'amount') {
        clearErrorState();
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        maxWidth='sm'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(onSubmit),
          },
        }}
      >
        <DialogTitle onClose={handleClose}>เลื่อนเวลาสอบ</DialogTitle>
        <Divider />
        <DialogContent>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
            <TimePostponementAlert errorMessage={errorState?.alertError} />

            <TimePostponementSessionTime sessions={originalSessions} />

            <Box sx={{ width: '318px' }}>
              <FormNumberInput
                name='amount'
                label='ระยะเวลาที่ต้องการเลื่อน (นาที)'
                placeholder='ระบุระยะเวลา'
                helperText='ขั้นต่ำ 15 นาที สูงสุด 120 นาที'
                required
              />
            </Box>

            <TimePostponementSessionPostponedTime
              sessions={postponedSessions}
              errorMessage={errorState?.sessionTimeError}
            />

            <FormTextarea
              name='remark'
              label='เหตุผลในการเลื่อน'
              required
              placeholder='กรุณากรอกเหตุผล'
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
            ยกเลิก
          </Button>
          <FormButton
            label='ยืนยันเลื่อนเวลาสอบ'
            loading={postponeExamEventTimeLoading}
            disabled={!formContext.formState.isValid}
            size='large'
          />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
